import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import {
  Tabs, SchemaField, CreateEntityModal, EntityContactsPanel, EntityGroupsPanel, EntityActivityFeed,
  EntityNotesPanel, EntityDocumentsPanel, JurisdictionCapModal, JurisdictionAnnualInfoPanel,
  CollectorStatementsPanel,
  DataTable, LastUpdated,
} from '../organisms';
import type { EntityContact } from '../organisms';
import { groupableTypeForTable, useGroupCount, groupTabLabel } from '../lib/groups';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';
import type { AttrMap } from '../lib/inlineAttribution';
import { InlineDetailFieldGrid, singularize } from './detailFields';

/**
 * GovernmentEntityDetail — /assessors/:id, /collectors/:id, /jurisdictions/:id.
 *
 * The tabbed profile for the three government reference entities, restoring the
 * bespoke EntityHeader + Tabs layout used by clients / parcels / properties (the
 * flat schema-driven GenericDetail was the regression). Structurally identical
 * across the three entities, so it is ONE config-driven page — main.tsx wiring
 * stays a single line each.
 *
 *   - Overview : the same schema-driven field grid GenericDetail renders
 *                (DetailFieldGrid — shared implementation).
 *   - Contacts : EntityContactsPanel over the live L2-crm contacts routes
 *                (contacts-by-entity / entity-contacts). Feature 3.4.
 *   - Activity : per-entity humanized audit feed (parity with client/parcel).
 *
 * Data queries are the SAME two GenericDetail runs (GET /api/schema/:table +
 * GET /api/:apiPath/:id) — FE-only, no backend or schema change.
 */

export interface GovernmentEntityDetailConfig {
  table: 'assessors' | 'collectors' | 'jurisdictions';
  title: string;                                      // plural label; prefix uses the singular
}

// contact/RBAC entity key per table (mirrors TABLE_TO_ENTITY in src/middleware/auth.js).
const ENTITY_FOR_TABLE: Record<GovernmentEntityDetailConfig['table'], 'assessor' | 'collector' | 'jurisdiction'> = {
  assessors: 'assessor',
  collectors: 'collector',
  jurisdictions: 'jurisdiction',
};

// The documents FK column each gov entity uses to scope its documents. The
// `documents` table carries assessor_id + collector_id, so those two profiles get
// a Documents tab with the SAME upload/download/filter pattern client/parcel use.
// Jurisdictions have NO documents FK column — a Documents tab there would need a
// schema change (blocked-on-schema), so it's intentionally absent.
const DOCUMENT_FK_FOR_TABLE: Record<GovernmentEntityDetailConfig['table'], 'assessor_id' | 'collector_id' | null> = {
  assessors: 'assessor_id',
  collectors: 'collector_id',
  jurisdictions: null,
};

// The FIXED standard set of reference links surfaced in the header per entity
// type. These are the record's OWN columns (schema.sql) — the official website
// plus the government's public property-search lookups — not a user-addable
// list. Only the fields the record actually populates render (see `headerLinks`
// below). Jurisdictions carry no website/lookup columns per the domain, so they
// surface none.
const LINK_FIELDS: Record<GovernmentEntityDetailConfig['table'], Array<{ field: string; label: string }>> = {
  assessors: [
    { field: 'website_url', label: 'Website' },
    { field: 're_lookup_url', label: 'RE property search' },
    { field: 'pp_lookup_url', label: 'PP property search' },
  ],
  collectors: [
    { field: 'website_url', label: 'Website' },
    { field: 're_lookup_url', label: 'RE search' },
    { field: 'pp_lookup_url', label: 'PP search' },
  ],
  jurisdictions: [],
};

// A parcel row from GET /api/parcels-by-jurisdiction/:id — the display columns
// the jurisdiction Parcels tab renders (account #, parcel name, property,
// client). `id` drives the row-click into the parcel detail page.
interface JurisdictionParcelRow {
  id: string;
  account_number: string | null;
  name: string | null;
  property_id: string | null;
  property_name: string | null;
  client_id: string | null;
  client_name: string | null;
}

/**
 * LinkChip — a small outlined chip that opens a government reference URL in a
 * new tab. `rel="noopener noreferrer"` so the opened page can't reach back
 * through `window.opener`. Styled with inline design tokens to match the header.
 */
function LinkChip({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
        padding: 'var(--sp-0-5) var(--sp-2)',
        borderRadius: 'var(--r-sm)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-card)',
        color: 'var(--brand-primary)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        textDecoration: 'none',
        lineHeight: 'var(--lh-tight)',
      }}
    >
      {label}
      <span aria-hidden="true">↗</span>
    </a>
  );
}

type Tab = 'overview' | 'annual' | 'parcels' | 'statements' | 'contacts' | 'groups' | 'notes' | 'documents' | 'activity';

export function GovernmentEntityDetailByConfig(config: GovernmentEntityDetailConfig) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useTabState<Tab>(['overview', 'annual', 'parcels', 'statements', 'contacts', 'groups', 'notes', 'documents', 'activity'] as const, 'overview');
  const groupCount = useGroupCount(groupableTypeForTable(config.table) as string, id);
  const [editOpen, setEditOpen] = useState(false);
  const [capOpen, setCapOpen] = useState(false);

  const apiPath = config.table.replace(/_/g, '-');
  const listPath = `/${apiPath}`;
  const documentFk = DOCUMENT_FK_FOR_TABLE[config.table];

  // Edit affordance renders only for holders of this entity's can_edit (SoD:
  // mirror the server gate; default-deny while the permission grid loads).
  const canEdit = useEntityPermission(ENTITY_FOR_TABLE[config.table], 'can_edit');

  const schemaQuery = useQuery<{ fields: SchemaField[] }>({
    queryKey: ['schema', config.table],
    queryFn: ({ signal }) => api.get(`/api/schema/${config.table}`, signal),
    staleTime: 5 * 60_000,
  });

  const recordQuery = useQuery<Record<string, unknown>>({
    queryKey: [config.table, id],
    queryFn: ({ signal }) => api.get(`/api/${apiPath}/${id}`, signal),
    enabled: !!id,
  });

  // Contacts count for the tab badge — shares EntityContactsPanel's EXACT query
  // key so there's one fetch and the panel's add/edit invalidation refreshes both.
  // The panel keys on ['contacts-by-entity', entityType, entityId, direct?'direct':'all']
  // (EntityContactsPanel.tsx:94-95); a government host (assessor/collector/jurisdiction)
  // is never a property/parcel, so direct=false → the 4th element is 'all'. Without
  // this 4th element the panel's 4-element invalidation never matched the badge.
  const contactsQuery = useQuery<EntityContact[]>({
    queryKey: ['contacts-by-entity', config.table, id, 'all'],
    queryFn: ({ signal }) => api.get<EntityContact[]>(`/api/contacts-by-entity/${config.table}/${id}`, signal),
    enabled: !!id,
  });

  // Notes count for the tab badge — shares EntityNotesPanel's query key so the
  // fetch is deduped and any add/edit/delete invalidation refreshes both.
  const notesQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['entity-notes', config.table, id],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/notes-enriched/${config.table}/${id}`, signal),
    enabled: !!id,
  });

  // Documents count for the tab badge (only for entities with a documents FK) —
  // shares EntityDocumentsPanel's query key so the fetch is deduped.
  const documentsQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['documents', documentFk, id],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/documents?${documentFk}=${id}`, signal),
    enabled: !!id && !!documentFk,
  });

  // Statements count for the collector tab badge — shares CollectorStatementsPanel's
  // query key so the fetch is deduped. Collector-only surface.
  const isCollector = config.table === 'collectors';
  const statementsQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['tax-statements', 'collector_id', id],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/tax-statements?collector_id=${id}`, signal),
    enabled: !!id && isCollector,
  });

  // Parcels within this jurisdiction — jurisdiction-only surface. Every parcel
  // that carries a tax line (tax_records.jurisdiction_id) in this jurisdiction,
  // org- + assignment-scoped by the endpoint. Drives the Parcels tab + its count.
  const isJurisdiction = config.table === 'jurisdictions';
  const parcelsQuery = useQuery<JurisdictionParcelRow[]>({
    queryKey: ['parcels-by-jurisdiction', id],
    queryFn: ({ signal }) => api.get<JurisdictionParcelRow[]>(`/api/parcels-by-jurisdiction/${id}`, signal),
    enabled: !!id && isJurisdiction,
  });

  // Per-section attribution for the inline-editable Overview (A12) — the latest
  // change to each Overview box's fields, keyed by table+id like the other profiles.
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', config.table, id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/${config.table}/${id}`, signal),
    enabled: !!id,
  });

  if (schemaQuery.isLoading || recordQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading {config.title.toLowerCase()}…</p>;
  }

  const error = schemaQuery.error || recordQuery.error;
  if (error) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>
          {notFound ? `${singularize(config.title)} not found` : `Failed to load ${config.title.toLowerCase()}`}
        </h2>
        <p style={{ margin: 0 }}>{(error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
            Back to {config.title}
          </Button>
        </p>
      </div>
    );
  }

  if (!schemaQuery.data || !recordQuery.data || !id) return null;

  const row = recordQuery.data;
  const attr: AttrMap = attrQuery.data ?? {};
  const contacts = contactsQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const documents = documentsQuery.data ?? [];
  const statements = statementsQuery.data ?? [];
  const parcels = parcelsQuery.data ?? [];

  // Display title: prefer `name`, then `account_number`, then a sliced id.
  const title =
    (typeof row.name === 'string' && row.name)
    || (typeof row.account_number === 'string' && row.account_number)
    || (typeof row.id === 'string' && row.id.slice(0, 8))
    || config.title;

  // The entity's state (e.g. "Fulton County, GA" reads as title + this) and the
  // standard reference links — both surfaced in the header via `subtitle`. Only
  // links the record actually populates render.
  const stateLabel = typeof row.state === 'string' && row.state ? row.state : null;
  const headerLinks = LINK_FIELDS[config.table]
    .map((l) => ({ ...l, href: typeof row[l.field] === 'string' ? (row[l.field] as string) : '' }))
    .filter((l) => l.href.length > 0);
  const hasSubtitle = stateLabel !== null || headerLinks.length > 0;

  return (
    <>
      <EntityHeader
        prefix={singularize(config.title)}
        title={String(title)}
        subtitle={
          hasSubtitle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              {stateLabel && <span>{stateLabel}</span>}
              {headerLinks.length > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                  {headerLinks.map((l) => (
                    <LinkChip key={l.field} href={l.href} label={l.label} />
                  ))}
                </span>
              )}
              <LastUpdated updatedBy={row.updated_by as string | null | undefined} updatedAt={row.updated_at as string | null | undefined} />
            </div>
          ) : (
            <LastUpdated updatedBy={row.updated_by as string | null | undefined} updatedAt={row.updated_at as string | null | undefined} />
          )
        }
        statusPills={
          typeof row.status === 'string' ? (
            <Badge variant="neutral">{row.status.replace(/_/g, ' ')}</Badge>
          ) : undefined
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
              Back
            </Button>
            {/* §8.3/§2.5: the Exemption Mass-Update capability lives here now — a
                jurisdiction-level cap that mass-updates every attached parcel. */}
            {canEdit && config.table === 'jurisdictions' && (
              <Button variant="secondary" size="sm" onClick={() => setCapOpen(true)}>
                Apply cap
              </Button>
            )}
            {canEdit && (
              <Button variant="primary" size="sm" onClick={() => setEditOpen(true)}>
                Edit all fields
              </Button>
            )}
          </>
        }
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as Tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          // Annual Information is a jurisdiction-only surface (§2): rate, ratio,
          // tax type, tax-year dates + the certified/millage flags.
          ...(isJurisdiction
            ? [{ value: 'annual', label: 'Annual Info' }]
            : []),
          // Parcels is a jurisdiction-only surface: every parcel that carries a
          // tax line in this jurisdiction (via tax_records).
          ...(isJurisdiction
            ? [{ value: 'parcels', label: 'Parcels', count: parcels.length }]
            : []),
          { value: 'contacts', label: 'Contacts', count: contacts.length },
          { value: 'groups', label: groupTabLabel(groupCount) },
          { value: 'notes', label: 'Notes', count: notes.length },
          // A0 — the installment statements this COLLECTOR issues. tax_statements is
          // keyed UNIQUE(collector_id, process_year, statement_number), so it has
          // always belonged here; it simply had no surface, which is why people went
          // looking for it under the client.
          ...(isCollector
            ? [{ value: 'statements', label: 'Statements', count: statements.length }]
            : []),
          ...(documentFk ? [{ value: 'documents', label: 'Documents', count: documents.length }] : []),
          { value: 'activity', label: 'Activity' },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && (
          <InlineDetailFieldGrid
            fields={schemaQuery.data.fields}
            row={row}
            routePath={apiPath}
            recordId={id}
            invalidateKeys={[[config.table, id], [`${apiPath}-list`], ['field-attribution', config.table, id]]}
            attr={attr}
            canEdit={canEdit}
          />
        )}
        {tab === 'annual' && isJurisdiction && (
          <JurisdictionAnnualInfoPanel jurisdictionId={id} canEdit={canEdit} />
        )}
        {tab === 'parcels' && isJurisdiction && (
          <JurisdictionParcelsTab
            parcels={parcels}
            loading={parcelsQuery.isLoading}
            error={parcelsQuery.error as Error | null}
            onRowClick={(p) => navigate(`/parcels/${p.id}`)}
          />
        )}
        {tab === 'contacts' && <EntityContactsPanel entityType={config.table} entityId={id} />}
        {tab === 'groups' && groupableTypeForTable(config.table) && <EntityGroupsPanel entityType={groupableTypeForTable(config.table) as string} entityId={id as string} />}
        {tab === 'notes' && <EntityNotesPanel entityType={config.table} entityId={id} />}
        {tab === 'statements' && isCollector && <CollectorStatementsPanel collectorId={id} />}
        {tab === 'documents' && documentFk && <EntityDocumentsPanel fkColumn={documentFk} entityId={id} />}
        {tab === 'activity' && <EntityActivityFeed entityType={config.table} entityId={id} />}
      </div>

      {!!id && (
        <CreateEntityModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          table={config.table}
          routePath={apiPath}
          recordId={id}
          invalidateKeys={[[`${apiPath}-list`], [config.table, id]]}
        />
      )}

      {config.table === 'jurisdictions' && (
        <JurisdictionCapModal
          open={capOpen}
          onClose={() => setCapOpen(false)}
          jurisdictionId={id}
          jurisdictionLabel={String(title)}
        />
      )}
    </>
  );
}

/**
 * JurisdictionParcelsTab — lists every parcel that carries a tax line in this
 * jurisdiction (backed by GET /api/parcels-by-jurisdiction/:id). Row-click opens
 * the parcel detail page. Columns: account #, parcel name, property, client.
 */
function JurisdictionParcelsTab({
  parcels, loading, error, onRowClick,
}: {
  parcels: JurisdictionParcelRow[];
  loading: boolean;
  error: Error | null;
  onRowClick: (p: JurisdictionParcelRow) => void;
}) {
  const columns = useMemo<ColumnDef<JurisdictionParcelRow>[]>(() => [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>
          {(getValue() as string | null) ?? '—'}
        </span>
      ),
    },
    { accessorKey: 'name', header: 'Parcel', cell: ({ getValue }) => (getValue() as string | null) ?? '—' },
    { accessorKey: 'property_name', header: 'Property', cell: ({ getValue }) => (getValue() as string | null) ?? '—' },
    { accessorKey: 'client_name', header: 'Client', cell: ({ getValue }) => (getValue() as string | null) ?? '—' },
  ], []);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading parcels…</p>;
  if (error) {
    return (
      <p style={{ color: 'var(--color-status-red)' }}>Failed to load parcels: {error.message}</p>
    );
  }
  if (parcels.length === 0) {
    return (
      <p style={{ color: 'var(--color-ink-3)' }}>No parcels are linked to this jurisdiction yet.</p>
    );
  }
  return <DataTable data={parcels} columns={columns} onRowClick={onRowClick} />;
}
